import React, { useState, useEffect} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';

// Styling
import 'react-datepicker/dist/react-datepicker.css';
import 'react-toastify/dist/ReactToastify.css';
import { BsQuestionCircleFill } from "react-icons/bs";
import { RiErrorWarningLine } from "react-icons/ri";
import { FormLayout, StyledForm, StyledFormGroup, StyledHeader, StyledContainer, StyledFormLabel, QAblock, ErrorFormTextMsg } from '../../../../shared/styles/CommonStyle';

// Redux
import { SupportRequestFormType } from '../../types/SupportRequestFormType';
import { postSupportRequestForm, resetSupportRequestForm } from '../../redux/SupportRequestFormSlice';

// shared
import { Header } from '../../../../shared/components/Header';
import { EInkModal } from '../../../../shared/components/EInkModal';
import { dateTime2String, handleSelectedDate} from '../../../../shared/utils/date';

export function SupportRequestFormPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showImg, setShowImg] = useState<boolean>(false);
  const [formData, setFormData] = useState<SupportRequestFormType>({
    username: '',
    email: '',
    phoneNumber: '',
    issue: '',
    setUpDetails: {airApiVersion: '', airAppVersion: '', connectionType: '', timeStamp: dateTime2String(new Date())},
    robotInfo: {robotAirId: '', robotNickname: ''},
    ipadInfo: {ipadModel: '', iosVersion: ''},
    expectedCorrectBehavior: '',
    actualBehavior: '',
    stepsToReproduce: '',
  });

  useEffect(() => {
    resetSupportRequestForm();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const hasEmptyRobotInfo = (formData.robotInfo.robotAirId.length !== 14) || !formData.robotInfo.robotNickname;

    if (hasEmptyRobotInfo) {
      toast.error('Please fill in robot AIR ID and robot name fields');
      return;
    }

    dispatch(postSupportRequestForm(formData));
    navigate('/result');
  };

  const handleRobotAirIdChange = (block: number, value: string) => {
    const robotInfo = formData.robotInfo;
    const currentId = robotInfo.robotAirId.split('-');
    currentId[block] = value.replace(/[^0-9a-zA-Z]/g, '').slice(0, 4);
    robotInfo.robotAirId = currentId.join('-');
    setFormData({ ...formData, robotInfo });
  };

  const handleRobotNicknameChange = (inputRobotNickname: string) => {
    const robotInfo = formData.robotInfo;
    robotInfo.robotNickname = inputRobotNickname;
    setFormData({ ...formData, robotInfo });
  };

  const handleSetupDetailsChange = (value: string, detail: string) => {
    const setUpDetails = formData.setUpDetails;
    switch (detail) {
      case "airApiVersion":
        setUpDetails.airApiVersion = value;
        break;
      case "airAppVersion":
        setUpDetails.airAppVersion = value;
        break;
      case "timeStamp":
        setUpDetails.timeStamp = value;
        break;
      default:
        setUpDetails.connectionType = value;
        break;
    }
    setFormData({ ...formData, setUpDetails })
  }

  const handleIpadInfoChange = (value: string, detail: string) => {
    const ipadInfo = formData.ipadInfo;
    switch (detail) {
      case "ipadModel":
        ipadInfo.ipadModel = value;
        break;
      case "iosVersion":
        ipadInfo.iosVersion = value;
        break;
      default:
        break;
    }
    setFormData({ ...formData, ipadInfo })
  }

  const handleQuestionSymbolClick = (value: boolean) => {
    setShowImg(value);
    return;
  };

  return (
    <FormLayout>
      <Header title="AIR Support Request"/>
      <StyledContainer>
        <StyledForm onSubmit={handleSubmit}>
          <StyledHeader>
            <b>Please check documentation on our <a href='https://advancedimagerobotics.com/support/' target='_blank'>support page</a> for a solution before submitting a ticket.</b>
          </StyledHeader>
          <StyledFormGroup>
            <QAblock>
              <Form.Label>Your Name:</Form.Label>
              <Form.Control
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              />
            </QAblock>
            <QAblock>
              <Form.Label>Your Email:</Form.Label>
              <Form.Control
                type="text"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </QAblock>
            <QAblock>
              <Form.Label>Your Phone Number:</Form.Label>
              <Form.Control
                type="text"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              />
            </QAblock>
          </StyledFormGroup>

          <StyledFormGroup>
            <Form.Label>Describe the issue</Form.Label>
            <Form.Control
              as="textarea"
              value={formData.issue}
              onChange={(e) => setFormData({ ...formData, issue: e.target.value })}
            />
          </StyledFormGroup>

          <StyledFormGroup>
            <QAblock>
              <StyledFormLabel>
                Robot AIR ID
                <BsQuestionCircleFill onClick={() => setShowImg(true)}/>
              </StyledFormLabel>
              <div className="d-flex">
                {[0, 1, 2].map((block) => (
                  <React.Fragment key={block}>
                    <Form.Control
                      value={formData.robotInfo.robotAirId.split('-')[block] || ''}
                      onChange={(e) => handleRobotAirIdChange(block, e.target.value)}
                      maxLength={4}
                    />
                    {block < 2 && <span className="mx-2">-</span>}
                  </React.Fragment>
                ))}
              </div>
              {formData.robotInfo.robotAirId.length !== 14 && (
                <ErrorFormTextMsg>
                  <RiErrorWarningLine/>Robot AIR ID should not be empty
                </ErrorFormTextMsg>
              )}
            </QAblock>
            <QAblock>
              <Form.Label>Robot Nickname:</Form.Label>
              <Form.Control
                type="text"
                value={formData.robotInfo.robotNickname}
                onChange={(e) => handleRobotNicknameChange(e.target.value)}
              />
              {!formData.robotInfo.robotNickname && (
                <ErrorFormTextMsg>
                  <RiErrorWarningLine/>Robot name should not be empty
                </ErrorFormTextMsg>
              )}
            </QAblock>
          </StyledFormGroup>

          <StyledFormGroup>
            <QAblock>
              <Form.Label>iPad model:</Form.Label>
              <Form.Control
                type="text"
                value={formData.ipadInfo.ipadModel}
                onChange={(e) => handleIpadInfoChange(e.target.value, 'ipadModel')}
              />
            </QAblock>
            <QAblock>
              <Form.Label>iOS version:</Form.Label>
              <Form.Control
                type="text"
                value={formData.ipadInfo.iosVersion}
                onChange={(e) => handleIpadInfoChange(e.target.value, 'iosVersion')}
              />
            </QAblock>
          </StyledFormGroup>

          <StyledFormGroup>
            <QAblock>
              <Form.Label>AIR API Version:</Form.Label>
              <Form.Control
                type="text"
                value={formData.setUpDetails.airApiVersion}
                onChange={(e) => handleSetupDetailsChange(e.target.value, 'airApiVersion')}
              />
            </QAblock>
            <QAblock>
              <Form.Label>AIR App Version:</Form.Label>
              <Form.Control
                type="text"
                value={formData.setUpDetails.airAppVersion}
                onChange={(e) => handleSetupDetailsChange(e.target.value, 'airAppVersion')}
              />
            </QAblock>
            <QAblock>
              <Form.Label>Connection Type: Tailscale & LAN</Form.Label>
              <Form.Control
                type="text"
                value={formData.setUpDetails.connectionType}
                onChange={(e) => handleSetupDetailsChange(e.target.value, 'connectionType')}
              />
            </QAblock>
            <QAblock>
              <Form.Label>Occurrence Time:</Form.Label>
              <div>
                <DatePicker
                  selected={handleSelectedDate(formData.setUpDetails.timeStamp)}
                  onChange={(time) => handleSetupDetailsChange(dateTime2String(time), 'timeStamp')}
                  showTimeSelect
                  timeIntervals={15}
                  dateFormat="Pp"
                  className="form-control"
                />
              </div>
            </QAblock>
          </StyledFormGroup>

          <StyledFormGroup>
            <Form.Label>Expected behavior (if applicable)</Form.Label>
            <Form.Control
              as="textarea"
              value={formData.expectedCorrectBehavior}
              onChange={(e) => setFormData({ ...formData, expectedCorrectBehavior: e.target.value })}
            />
          </StyledFormGroup>

          <StyledFormGroup>
            <Form.Label>Actual behavior (if applicable)</Form.Label>
            <Form.Control
              as="textarea"
              value={formData.actualBehavior}
              onChange={(e) => setFormData({ ...formData, actualBehavior: e.target.value })}
            />
          </StyledFormGroup>

          <StyledFormGroup>
            <Form.Label>Steps to reproduce (if applicable)</Form.Label>
            <Form.Control
              as="textarea"
              value={formData.stepsToReproduce}
              onChange={(e) => setFormData({ ...formData, stepsToReproduce: e.target.value })}
            />
          </StyledFormGroup>

          <EInkModal showImg={showImg} onBtnClick={handleQuestionSymbolClick}/>

          <Row>
            <Col>
              <Button type="submit" variant="primary">Submit</Button>
            </Col>
          </Row>

        </StyledForm>
      </StyledContainer>
    </FormLayout>
  );
}
